import React, { useCallback, useRef, useState } from 'react'
import {
  Card,
  Form,
  FormLayout,
  Page,
  Modal,
  Button,
  Spinner,
  SkeletonBodyText, Link, FooterHelp, Banner
} from '@shopify/polaris'
import { useDispatch, useSelector } from 'react-redux'
import isEqual from 'react-fast-compare'
import axios from 'axios'
import { useHistory } from 'react-router-dom'

import { mapOrder, arrayMove } from '../../libs/utils'

import {
  actionSetSelectedItem,
  actionSetToastMessage,
  actionSetToastError,
  actionToggleToastActive,
  actionUpdateItemField
} from '../../store/actions'

import useShouldSendRequest from '../../hooks/useShouldSendRequest'

import TextInputComponent from '../../components/text-input'
import CheckboxComponent from '../../components/checkbox-input'
import SelectComponent from '../../components/select-input'
import MapperListComponent from '../../components/mapper-list/mapper-list'
import ItemListComponent from '../../components/item-list'
import ContextualSaveBarComponent from '../../components/custom-contextual-save-bar'
import PromptAlertComponent from '../../components/prompt-alert'

const EditSliderComponent = (props) => {
  const history = useHistory()
  const editingId = props.match ? props.match.params.id : false
  const dispatch = useDispatch()
  const activeItem = useSelector(state => state.activeItem)
  const originalItem = useRef({...activeItem})
  const [modalActive, setModalActive] = useState(false)
  const [loading, setLoading] = useState(false)
  const shouldLoading = useShouldSendRequest(
    `/api/v2/admin/slider/${editingId}`,
    editingId && !Object.keys(activeItem).length,
    (data) => {
      originalItem.current = data
    }
  )

  const mapperOrder = activeItem.mapper_order ? activeItem.mapper_order.split(',').map(Number) : []
  const orderedMappers = activeItem.mappers ? mapOrder(activeItem.mappers, mapperOrder, 'id') : []

  const handleModalChange = useCallback(() => setModalActive(!modalActive), [modalActive])
  const activator = <Button primary onClick={handleModalChange}>Add or Remove mappers</Button>

  const isMapperAdded = (id) => {
    return (activeItem.mappers || []).filter(item => parseInt(item.id) === parseInt(id)).length > 0
  }

  const handleSelectedMappers = (item) => {
    const addedMappers = activeItem.mappers || []
    const updatedMappers = (isMapperAdded(item.id)) ?
      [...addedMappers].filter(mapper => mapper.id !== item.id) :
      addedMappers.concat([item])
    dispatch(actionUpdateItemField('mappers', updatedMappers))
    const mapperOrders = updatedMappers.map(mapper => mapper.id).join(',')
    dispatch(actionUpdateItemField('mapper_order', mapperOrders))
  }

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newMappers = arrayMove([...activeItem.mappers], oldIndex, newIndex)
    dispatch(actionUpdateItemField('mappers', newMappers))
    const mapperOrders = newMappers.map(mapper => mapper.id).join(',')
    dispatch(actionUpdateItemField('mapper_order', mapperOrders))
  }

  const saveAction = (shouldRedirectToGetCode = false) => {
    setLoading(true)
    const sliderData = {
      ...activeItem,
      mappers: [],
      mapper_ids: ({ ...activeItem }.mappers || []).map(mapper => mapper.id)
    }
    let endpoint, data
    if (editingId) {
      endpoint = '/api/v2/admin/sliders/save'
      data = {
        id: editingId,
        slider: sliderData
      }
    } else {
      endpoint = '/api/v2/admin/sliders/new'
      data = {
        slider: sliderData
      }
    }
    axios.post(endpoint, data)
      .then((res) => {
        originalItem.current = activeItem
        setLoading(false)
        if (shouldRedirectToGetCode) {
          history.push(`/sliders/${res.data.id}/code`)
        }
      })
      .catch((e) => {
        console.log(e)
      })
  }

  return (
    <div>
      <Page fullWidth>
        <Banner status="info">
          <p>Please note that because modern browsers cache the javascript files, it may take some time for your storefront to reflect saved changes, however, you can always see the new changes in the private/incognito window of your browser as private window is never cached by the browser.</p>
        </Banner>
      </Page>
      <Page
        fullWidth
        breadcrumbs={[{
          content: 'Sliders',
          url: '/sliders'
        }]}
        title={editingId ? 'Edit slider' : 'Add slider'}
      >
        {shouldLoading && <SkeletonBodyText />}
        {!shouldLoading && <div className={loading ? 'loading-wrapper' : null}>
          {loading && <Spinner accessibilityLabel="Saving data" size="large" color="teal" />}
          {!isEqual(originalItem.current, activeItem) && <ContextualSaveBarComponent
            onDiscardAction={() => {
              dispatch(actionSetSelectedItem(originalItem.current))
            }}
            onSaveAction={() => {
              saveAction(!editingId)
              dispatch(actionSetToastMessage(editingId ? 'Slider saved' : 'Slider created'))
              dispatch(actionSetToastError(false))
              dispatch(actionToggleToastActive(true))
            }}
          />}
          <PromptAlertComponent when={!isEqual(originalItem.current, activeItem)}/>
          <Card sectioned>
            <Form noValidate onSubmit={() => {}}>
              <FormLayout>
                <TextInputComponent
                  name='name'
                  value={activeItem.name}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                  label='Slider name'
                />
                <CheckboxComponent
                  label='Autoplay?'
                  name='autoplay'
                  checked={activeItem.autoplay}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                {activeItem.autoplay && <SelectComponent
                  label='Autoplay speed'
                  options={[
                    { label: '1s', value: '1000' },
                    { label: '2s', value: '2000' },
                    { label: '3s', value: '3000' },
                    { label: '4s', value: '4000' },
                    { label: '5s', value: '5000' },
                    { label: '6s', value: '6000' },
                    { label: '7s', value: '7000' },
                    { label: '8s', value: '8000' },
                    { label: '9s', value: '9000' },
                    { label: '10s', value: '10000' },
                    { label: '12s', value: '12000' },
                    { label: '13s', value: '13000' },
                    { label: '20s', value: '20000' },
                    { label: '25s', value: '25000' },
                    { label: '30s', value: '30000' },
                  ]}
                  name='speed'
                  value={activeItem.speed}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />}
                <CheckboxComponent
                  label='Show dots?'
                  name='dots'
                  checked={activeItem.dots}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                <CheckboxComponent
                  label='Show arrows (next and previous button)?'
                  name='arrows'
                  checked={activeItem.arrows}
                  onChange={(key, value) => dispatch(actionUpdateItemField(key, value))}
                />
                {activator}
                <Modal
                  activator={activator}
                  open={modalActive}
                  title="Select mapper"
                  onClose={handleModalChange}
                >
                  <ItemListComponent
                    onClick={(item) => {
                      handleSelectedMappers(item)
                    }}
                    customShortcutActions={(item => {
                      return [
                        {
                          content: isMapperAdded(item.id) ? 'Remove' : 'Add',
                          onAction: () => handleSelectedMappers(item)
                        }
                      ]
                    })}
                    resourceName={{
                      singular: 'mapper',
                      plural: 'mappers'
                    }}
                    endpoint={`/api/v2/admin/mappers`}
                  />
                </Modal>
                <MapperListComponent list={orderedMappers} onSortEnd={onSortEnd}/>
              </FormLayout>
            </Form>
          </Card>
        </div>}
        <FooterHelp>
          Learn more about<Link url="/docs#/slider-settings" external> slider settings</Link>
        </FooterHelp>
      </Page>
    </div>
  )
}

export default EditSliderComponent
